import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

export type TrialUrgency = "none" | "info" | "warning" | "urgent";

export interface TrialStatus {
  status: "trial" | "active" | "expired" | "cancelled" | null;
  daysRemaining: number | null;
  hoursRemaining: number | null;
  trialEndsAt: Date | null;
  isLoading: boolean;
  urgency: TrialUrgency;
  isInTrial: boolean;
}

function calculateUrgency(daysRemaining: number | null): TrialUrgency {
  if (daysRemaining === null) return "none";
  if (daysRemaining > 7) return "none";
  if (daysRemaining >= 4) return "info";
  if (daysRemaining >= 1) return "warning";
  return "urgent"; // Less than 24 hours
}

export function useTrialStatus(): TrialStatus {
  const { user, isAuthenticated } = useAuthContext();

  const { data, isLoading } = useQuery({
    queryKey: ["trial-status", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("subscription_status, trial_ends_at")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Failed to fetch trial status:", error);
        return null;
      }

      return profile;
    },
    enabled: isAuthenticated && !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  // Default state when not authenticated or loading
  if (!isAuthenticated || !data) {
    return {
      status: null,
      daysRemaining: null,
      hoursRemaining: null,
      trialEndsAt: null,
      isLoading,
      urgency: "none",
      isInTrial: false,
    };
  }

  const status = data.subscription_status as TrialStatus["status"];
  const trialEndsAt = data.trial_ends_at ? new Date(data.trial_ends_at) : null;

  // Calculate time remaining
  let daysRemaining: number | null = null;
  let hoursRemaining: number | null = null;

  if (trialEndsAt && status === "trial") {
    const now = new Date();
    const diffMs = trialEndsAt.getTime() - now.getTime();
    
    if (diffMs > 0) {
      daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      hoursRemaining = Math.ceil(diffMs / (1000 * 60 * 60));
    } else {
      daysRemaining = 0;
      hoursRemaining = 0;
    }
  }

  const urgency = calculateUrgency(daysRemaining);
  const isInTrial = status === "trial" && daysRemaining !== null && daysRemaining > 0;

  return {
    status,
    daysRemaining,
    hoursRemaining,
    trialEndsAt,
    isLoading,
    urgency,
    isInTrial,
  };
}
